import { PageViewModel } from "./ViewModels/PageViewModel";
import { DashJobService } from "../../../services/DashJobService";
import { useEffect, useMemo, useState } from "react";
import { PageView } from "./Views/PageView";
import { useAppDispatch, useAppSelector, useDarkMode } from "redux/hooks";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { useSession } from "components/wrappers/SessionProvider";
import { endSubmitting, requestCompleted, requestStart } from "redux/slices/app";
import { RefreshAction } from "redux/actions/Refresh";
import { signOut } from "redux/slices/user";
import { RunNLIPageWrapper } from "./styles";
import { useViewModel } from "mvvm/View";

export default function RunDashPage() {

    const dispatch = useAppDispatch();
    const user = useAppSelector(state => state.user);
    const modelInfo = user.models[user.modelIndex];
    const pageLocation = useLocation().pathname;
    const session = useSession();
    const darkMode = useDarkMode();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const requestJobID = searchParams.get('request') || undefined;

    const dashJobService = useMemo(() => new DashJobService(dispatch, session, modelInfo, user.userID), []);
    const pageViewModel = useMemo(() => new PageViewModel(dashJobService, modelInfo.alias, requestJobID), []);
    const reactiveViewModel = useViewModel(pageViewModel);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isScheduleOpen, setIsScheduleOpen] = useState(false);
    const [isQueriesOpen, setIsQueriesOpen] = useState(false);
    const [isAddQueryOpen, setIsAddQueryOpen] = useState(false);
    const [queriesInitialQuery, setQueriesInitialQuery] = useState<string | undefined>(undefined);
    const [queriesInitialQueryName, setQueriesInitialQueryName] = useState<string | undefined>(undefined);

    useEffect(() => {
        dashJobService.dispatch = dispatch;
        dashJobService.session = session;
        dashJobService.model = modelInfo;
    }, [dispatch, session, modelInfo, dashJobService]);

    useEffect(() => {
        dispatch(requestCompleted());
        dispatch(endSubmitting());
    }, [dispatch]);

    // Sync URL when selectedJob changes (using reactiveViewModel to get updates)
    useEffect(() => {
        const selectedJobID = reactiveViewModel.selectedJob?.jobID;
        const currentRequestParam = searchParams.get('request');

        if (selectedJobID) {
            // Job is selected - update URL if needed
            if (currentRequestParam !== selectedJobID) {
                navigate({ search: `?request=${selectedJobID}` }, { replace: true });
            }
        } else {
            // Job is deselected - remove request parameter if present
            if (currentRequestParam) {
                navigate({ search: '' }, { replace: true });
            }
        }
    }, [reactiveViewModel.selectedJob?.jobID, navigate, searchParams]);

    // Handle URL parameter changes (e.g., browser back/forward)
    useEffect(() => {
        const requestParam = searchParams.get('request');
        const currentSelectedJobID = reactiveViewModel.selectedJob?.jobID;

        if (requestParam && requestParam !== currentSelectedJobID) {
            // URL has a request parameter that doesn't match current selection
            // Try to find and select the job
            const jobToSelect = reactiveViewModel.jobs.find(j => j.jobID === requestParam);
            if (jobToSelect) {
                reactiveViewModel.selectedJob = jobToSelect;
            }
            // If job doesn't exist, silently ignore (edge case handled)
        } else if (!requestParam && currentSelectedJobID) {
            // URL has no request parameter but a job is selected
            // This shouldn't happen normally, but handle it gracefully
            // Don't deselect - let the other useEffect handle URL sync
        }
    }, [searchParams, reactiveViewModel]);

    const cleanup = async() => {
        pageViewModel.stopTimer();
        if (pageViewModel.jobListInvalidated) {
            dispatch(requestStart());
            await dispatch(RefreshAction(session));
            dispatch(requestCompleted());
        }
    }

    useEffect(() => {
        return () => {
            if (window.location.pathname !== pageLocation) {
                cleanup().catch(console.error);
            }
        };
    }, [pageLocation]);

    const handleLogout = () => {
        dispatch(signOut());
        session.logout();
    };

    return (
        <RunNLIPageWrapper className="fill negate-content-padding text-content flex-1 flex-column">
            <style>{`
                ${darkMode ? `
                    /* Global scrollbar styling for dark mode */
                    ::-webkit-scrollbar {
                        width: 10px;
                        height: 10px;
                    }
                    ::-webkit-scrollbar-track {
                        background: #1f1f1f;
                    }
                    ::-webkit-scrollbar-thumb {
                        background: #555555;
                        border-radius: 5px;
                    }
                    ::-webkit-scrollbar-thumb:hover {
                        background: #666666;
                    }
                    /* Firefox scrollbar */
                    * {
                        scrollbar-width: thin;
                        scrollbar-color: #555555 #1f1f1f;
                    }
                ` : `
                    /* Global scrollbar styling for light mode */
                    ::-webkit-scrollbar {
                        width: 10px;
                        height: 10px;
                    }
                    ::-webkit-scrollbar-track {
                        background: #f1f1f1;
                    }
                    ::-webkit-scrollbar-thumb {
                        background: #b0b0b0;
                        border-radius: 5px;
                    }
                    ::-webkit-scrollbar-thumb:hover {
                        background: #909090;
                    }
                    /* Firefox scrollbar */
                    * {
                        scrollbar-width: thin;
                        scrollbar-color: #b0b0b0 #f1f1f1;
                    }
                `}
                .ant-notification .ant-notification-notice {
                    padding: 8px 12px !important;
                    min-width: 200px !important;
                    max-width: 300px !important;
                }
                .ant-notification .ant-notification-notice-message {
                    font-size: 13px !important;
                    margin-bottom: 4px !important;
                    line-height: 1.4 !important;
                }
                .ant-notification .ant-notification-notice-description {
                    font-size: 12px !important;
                    line-height: 1.4 !important;
                }
                .ant-notification .ant-notification-notice-icon {
                    font-size: 16px !important;
                }
                .ant-notification .ant-notification-notice-close {
                    top: 8px !important;
                    right: 8px !important;
                }
                ${darkMode ? `
                    .ant-notification {
                        color: #ffffff !important;
                    }
                    .ant-notification .ant-notification-notice {
                        background-color: #2d2d2d !important;
                        border: 1px solid #404040 !important;
                        box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.48) !important;
                        border-radius: 6px !important;
                    }
                    .ant-notification .ant-notification-notice-message {
                        color: #ffffff !important;
                    }
                    .ant-notification .ant-notification-notice-description {
                        color: #cccccc !important;
                    }
                    .ant-notification .ant-notification-notice-icon {
                        color: #ffffff !important;
                    }
                    .ant-notification .ant-notification-notice-close {
                        color: #ffffff !important;
                    }
                    .ant-notification .ant-notification-notice-close:hover {
                        color: #cccccc !important;
                    }
                    .ant-notification-notice-success .ant-notification-notice-icon {
                        color: #52c41a !important;
                    }
                    .ant-notification-notice-info .ant-notification-notice-icon {
                        color: #1890ff !important;
                    }
                    .ant-notification-notice-warning .ant-notification-notice-icon {
                        color: #faad14 !important;
                    }
                    .ant-notification-notice-error .ant-notification-notice-icon {
                        color: #ff4d4f !important;
                    }
                ` : ''}
            `}</style>
            <PageView
                model={pageViewModel}
                allModels={user.models}
                currentModelAlias={modelInfo.alias}
                session={session}
                onLogout={handleLogout}
                isSettingsOpen={isSettingsOpen}
                onSettingsToggle={() => setIsSettingsOpen(!isSettingsOpen)}
                isScheduleOpen={isScheduleOpen}
                onScheduleToggle={() => setIsScheduleOpen(!isScheduleOpen)}
                isQueriesOpen={isQueriesOpen}
                onQueriesToggle={() => setIsQueriesOpen(!isQueriesOpen)}
                isAddQueryOpen={isAddQueryOpen}
                queriesInitialQuery={queriesInitialQuery}
                queriesInitialQueryName={queriesInitialQueryName}
                onAddQueryToggle={(query?: string, queryName?: string) => {
                    if (query !== undefined) {
                        // If query is provided, update the initial values
                        setQueriesInitialQuery(query);
                        setQueriesInitialQueryName(queryName);
                        // Only toggle if modal is closed
                        if (!isAddQueryOpen) {
                            setIsAddQueryOpen(true);
                        }
                    } else {
                        // Clear initial values when opening for new query
                        setQueriesInitialQuery(undefined);
                        setQueriesInitialQueryName(undefined);
                        setIsAddQueryOpen(prev => !prev);
                    }
                }}
            />
        </RunNLIPageWrapper>
    );
}